/**
 * Drives the robo-cat's "alive" behaviour (src/core/cat-mood.ts): any interaction in the app
 * keeps it awake, it dozes off when nothing happens, a tap startles or wakes it, and poking
 * it repeatedly makes it angry. One timer per cat, scheduled for the next state deadline.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { catMoodReducer, initialMood, nextDeadline, type CatPhase } from '../core/cat-mood.ts';
import { playFeedback } from './feedback.ts';

/** Activity is reported at most this often, so scrolling never causes a render storm. */
const ACTIVITY_THROTTLE_MS = 1_000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchmove', 'scroll'] as const;

export function useCatMood(enabled: boolean): { phase: CatPhase; poke: () => void } {
  const [mood, dispatch] = useReducer(catMoodReducer, 0, () => initialMood(Date.now()));
  const lastReported = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    // Coming back to idle (e.g. after "thinking") counts as activity, not as time asleep.
    dispatch({ type: 'activity', at: Date.now() });
    const onActivity = () => {
      const now = Date.now();
      if (now - lastReported.current < ACTIVITY_THROTTLE_MS) return;
      lastReported.current = now;
      dispatch({ type: 'activity', at: now });
    };
    const options = { passive: true, capture: true } as const;
    for (const type of ACTIVITY_EVENTS) window.addEventListener(type, onActivity, options);
    return () => {
      for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, onActivity, options);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const deadline = nextDeadline(mood);
    if (deadline === null) return;
    const timer = window.setTimeout(
      () => dispatch({ type: 'tick', at: Date.now() }),
      Math.max(0, deadline - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [enabled, mood]);

  const phaseRef = useRef(mood.phase);
  phaseRef.current = mood.phase;

  const poke = useCallback(() => {
    const before = phaseRef.current;
    const now = Date.now();
    lastReported.current = now;
    dispatch({ type: 'tap', at: now });
    playFeedback(before === 'asleep' ? 'wake' : 'poke');
  }, []);

  // Grumble once when the cat gets angry.
  const wasAngry = useRef(false);
  useEffect(() => {
    const angry = mood.phase === 'angry';
    if (angry && !wasAngry.current) playFeedback('grumble');
    wasAngry.current = angry;
  }, [mood.phase]);

  return { phase: enabled ? mood.phase : 'awake', poke };
}
